import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  LinkIcon,
  EyeIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  PlusIcon,
  ArrowRightIcon,
  ClipboardDocumentIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import { userService, linksService } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import toast from "react-hot-toast";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function Dashboard() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [isShorteningQuick, setIsShorteningQuick] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const response = await userService.getDashboard();

      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      toast.error("Dashboard verileri yüklenirken bir hata oluştu");
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickShorten = async (e) => {
    e.preventDefault();

    if (!url.trim()) {
      toast.error("Lütfen geçerli bir URL girin");
      return;
    }

    setIsShorteningQuick(true);
    try {
      const response = await linksService.create({ originalUrl: url });

      if (response.data.success) {
        const shortenedUrl = response.data.data.shortUrl;
        await navigator.clipboard.writeText(shortenedUrl);
        toast.success("Link kısaltıldı ve kopyalandı!");
        setUrl("");
        loadDashboardData(); // Refresh data
      }
    } catch (error) {
      toast.error("Link kısaltılırken bir hata oluştu");
    } finally {
      setIsShorteningQuick(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Kopyalandı!");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Dashboard yükleniyor..." />
      </div>
    );
  }

  // Chart data for earnings
  const chartData = {
    labels:
      dashboardData?.earningsChart?.map((item) =>
        new Date(item.date).toLocaleDateString("tr-TR", {
          month: "short",
          day: "numeric",
        })
      ) || [],
    datasets: [
      {
        label: "Günlük Tıklamalar",
        data: dashboardData?.earningsChart?.map((item) => item.clicks) || [],
        borderColor: "rgb(181, 187, 5)",
        backgroundColor: "rgba(181, 187, 5, 0.1)",
        tension: 0.1,
      },
      {
        label: "Günlük Kazanç ($)",
        data: dashboardData?.earningsChart?.map((item) => item.earnings) || [],
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Son 30 Gün Kazanç Trendi",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Hoş geldiniz, {user?.firstName}! 👋
        </h1>
        <p className="text-blue-100">
          Dashboard'ınızdan linklerinizi yönetin ve kazançlarınızı takip edin.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-50">
                <LinkIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Link</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {dashboardData?.totalLinks || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-green-50">
                <EyeIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Toplam Tıklama
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {dashboardData?.totalClicks?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-yellow-50">
                <CurrencyDollarIcon className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Toplam Kazanç
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${dashboardData?.totalEarnings?.toFixed(2) || "0.00"}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-50">
                <CalendarIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Bu Ay Kazanç
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${dashboardData?.thisMonthEarnings?.toFixed(2) || "0.00"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Link Shortener */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">
            Hızlı Link Kısaltma
          </h2>
        </div>
        <div className="card-body">
          <form
            onSubmit={handleQuickShorten}
            className="flex flex-col sm:flex-row gap-4"
          >
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Kısaltmak istediğiniz URL'i girin..."
              className="form-input flex-1"
              disabled={isShorteningQuick}
            />
            <button
              type="submit"
              disabled={isShorteningQuick || !url.trim()}
              className="btn btn-primary whitespace-nowrap"
            >
              {isShorteningQuick ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2"></div>
                  Kısaltılıyor...
                </>
              ) : (
                <>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Hızlı Kısalt
                </>
              )}
            </button>
          </form>
          <p className="text-sm text-gray-500 mt-2">
            💡 Kısaltılan link otomatik olarak panoya kopyalanacak
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Earnings Chart */}
        <div className="card lg:col-span-2">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">
              Kazanç Trendi
            </h2>
          </div>
          <div className="card-body">
            {dashboardData?.earningsChart?.length > 0 ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ChartBarIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Henüz kazanç verisi bulunmuyor</p>
                <p className="text-sm">
                  Linklerinizi paylaşın ve kazanç elde etmeye başlayın
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Links */}
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Son Linkler</h2>
            <Link to="/dashboard/links" className="btn btn-secondary btn-sm">
              Tümünü Gör
              <ArrowRightIcon className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="card-body">
            {dashboardData?.recentLinks?.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.recentLinks.map((link) => (
                  <div
                    key={link.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {link.title || "Başlıksız"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {process.env.REACT_APP_BASE_URL}/{link.shortCode}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <span>{link.clickCount} tık</span>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            `${process.env.REACT_APP_BASE_URL}/${link.shortCode}`
                          )
                        }
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <LinkIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Henüz link oluşturmamışsınız</p>
                <Link
                  to="/dashboard/links/create"
                  className="btn btn-primary btn-sm mt-4"
                >
                  İlk Linkinizi Oluşturun
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Referral Stats */}
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              Referans İstatistikleri
            </h2>
            <Link
              to="/dashboard/referrals"
              className="btn btn-secondary btn-sm"
            >
              Detaylar
              <ArrowRightIcon className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {dashboardData?.referralStats?.totalReferrals || 0}
                </p>
                <p className="text-sm text-gray-600">Toplam Referans</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  $
                  {dashboardData?.referralStats?.referralEarnings?.toFixed(2) ||
                    "0.00"}
                </p>
                <p className="text-sm text-gray-600">Referans Kazancı</p>
              </div>
            </div>

            {user?.referralCode && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Referans Linkiniz:</p>
                <div className="flex items-center justify-between">
                  <code className="text-sm text-primary-600 flex-1 truncate">
                    {process.env.REACT_APP_BASE_URL}/register?ref=
                    {user.referralCode}
                  </code>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `${process.env.REACT_APP_BASE_URL}/register?ref=${user.referralCode}`
                      )
                    }
                    className="ml-2 p-1 hover:bg-gray-200 rounded"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/dashboard/links/create"
          className="card hover:shadow-md transition-shadow"
        >
          <div className="card-body text-center">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PlusIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              Yeni Link Oluştur
            </h3>
            <p className="text-sm text-gray-600">
              Detaylı ayarlarla link oluşturun
            </p>
          </div>
        </Link>

        <Link
          to="/dashboard/earnings"
          className="card hover:shadow-md transition-shadow"
        >
          <div className="card-body text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              Kazanç Detayları
            </h3>
            <p className="text-sm text-gray-600">
              Detaylı kazanç raporlarını görün
            </p>
          </div>
        </Link>

        <Link
          to="/dashboard/referrals"
          className="card hover:shadow-md transition-shadow"
        >
          <div className="card-body text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LinkIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              Referans Sistemi
            </h3>
            <p className="text-sm text-gray-600">
              Arkadaş davet edin, komisyon kazanın
            </p>
          </div>
        </Link>
      </div>

      {/* Balance Info */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Mevcut Bakiye
            </h3>
            <p className="text-3xl font-bold text-green-600 mb-1">
              ${user?.availableBalance?.toFixed(2) || "0.00"}
            </p>
            <p className="text-sm text-gray-600">
              Toplam kazanç: ${user?.totalEarnings?.toFixed(2) || "0.00"}
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Link to="/dashboard/earnings" className="btn btn-success">
              Ödeme Talep Et
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
